// API configuration for the Columbus Multi-Agent System Dashboard
export const API_CONFIG = {
  // For production builds in containers, use empty string for relative URLs
  // The build sets REACT_APP_API_BASE_URL to "RELATIVE" to indicate relative URLs should be used
  BASE_URL: process.env.REACT_APP_API_BASE_URL === 'RELATIVE'
    ? ''
    : (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5226'),

  // Feature flags
  FEATURES: {
    // Enable/disable chat mode feature
    // Can be controlled via REACT_APP_ENABLE_CHAT_MODE environment variable
    ENABLE_CHAT_MODE: process.env.REACT_APP_ENABLE_CHAT_MODE === 'true',
  },

  ENDPOINTS: {
    // Health endpoints
    HEALTH: '/health',
    READY: '/api/v1/ready',

    // Query endpoints - Main endpoints for Columbus.Agents.Api
    QUERY_SUBMIT: '/api/v1/query',
    QUERY_STATUS: '/api/v1/query/{id}/status',
    QUERY_RESULT: '/api/v1/query/{id}/result',
    QUERY_STREAM: '/api/v1/query/{id}/stream',
    QUERY_FOLLOWUP: '/api/v1/query/{id}/followup',

    // Legacy endpoints (may not be implemented yet)
    // Agent endpoints
    AGENTS: '/api/v1/agents',
    AGENT_BY_ID: '/api/v1/agents/{id}',
    AGENTS_HEALTH: '/api/v1/agents/health',
    AGENT_HEALTH: '/api/v1/agents/health/{id}',
    AGENTS_METRICS: '/api/v1/agents/metrics',
    AGENTS_TOKEN_METRICS: '/api/v1/agents/metrics/tokens',

    // Project endpoints
    PROJECTS: '/api/v1/projects/',
    PROJECT_BY_ID: '/api/v1/projects/{id}',
    PROJECT_QUERY: '/api/v1/projects/{id}/query',
    PROJECT_REFRESH: '/api/v1/projects/{id}/refresh',
    PROJECT_HISTORY: '/api/v1/projects/{id}/history',
    PROJECT_CHAT_MESSAGES: '/api/v1/projects/{id}/chat-messages',

    // Auth endpoints (contracts.md §8.1)
    AUTH_LOGIN: '/api/v1/auth/login',
    AUTH_LOGOUT: '/api/v1/auth/logout',
    AUTH_ME: '/api/v1/auth/me',
    AUTH_ME_PROFILE: '/api/v1/auth/me/profile',
    USERS: '/api/v1/users/',

    // Live meeting endpoints (contracts.md §8.2 / §8.3 / §8.4 / §8.5)
    MEETING_LIVE_START: '/api/v1/meetings/{id}/live:start',
    MEETING_LIVE_STOP: '/api/v1/meetings/{id}/live:stop',
    MEETING_LIVE_SLIDE: '/api/v1/meetings/{id}/live/slide',
    MEETING_LIVE_TRANSCRIBER: '/api/v1/meetings/{id}/live/transcriber',
    MEETING_LIVE_TEAMS_TOKEN: '/api/v1/meetings/{id}/live/teams-token',
    MEETING_LIVE_SPEECH_TOKEN: '/api/v1/meetings/{id}/live/speech-token',
    MEETING_LIVE_STREAM: '/api/v1/meetings/{id}/live/stream',
    MEETING_TRANSCRIPT_SEGMENTS: '/api/v1/meetings/{id}/transcript/segments',
    MEETING_INSIGHTS: '/api/v1/meetings/{id}/insights',
    MEETING_INSIGHTS_REGENERATE: '/api/v1/meetings/{id}/insights:regenerate',
    MEETING_QUESTIONS: '/api/v1/meetings/{id}/questions',
    MEETING_QUESTION_ACT: '/api/v1/meetings/{id}/questions/{qid}:act',
    MEETING_BRIEFING_PERSONA: '/api/v1/meetings/{id}/briefing/persona',
    MEETING_CHAT_STREAM: '/api/v1/meetings/{id}/chat/stream',
    MEETING_OUTCOMES_APPROVE: '/api/v1/meetings/{id}/outcomes:approve',
    MEETING_OUTCOMES_EXPORT_PDF: '/api/v1/meetings/{id}/outcomes/export/pdf',
    MEETING_OUTCOMES_EXPORT_PPTX: '/api/v1/meetings/{id}/outcomes/export/pptx',

    // Meeting endpoints
    MEETINGS: '/api/v1/meetings/',
    MEETING_BY_ID: '/api/v1/meetings/{id}',
    MEETING_AGENDA: '/api/v1/meetings/{id}/agenda',
    MEETING_AGENDA_GENERATE: '/api/v1/meetings/{id}/agenda/generate',
    MEETING_ATTENDEES_GENERATE: '/api/v1/meetings/{id}/attendees/generate',
    MEETING_BRIEFING_GENERATE: '/api/v1/meetings/{id}/briefing/generate',
    MEETING_TRANSCRIPT: '/api/v1/meetings/{id}/transcript',
    MEETING_SUMMARY: '/api/v1/meetings/{id}/summary',
    MEETING_SUMMARY_GENERATE: '/api/v1/meetings/{id}/summary/generate',
    MEETING_CHAT: '/api/v1/meetings/{id}/chat',

    // File endpoints
    PROJECT_FILE_UPLOAD: '/api/v1/files/project/{id}/upload',
    PROJECT_FILES: '/api/v1/files/project/{id}',
    FILE_PROCESS: '/api/v1/files/{id}/process',
    FILE_UPLOAD_GLOBAL: '/api/v1/files/upload',
    FILES: '/api/v1/files/',
    FILE_UPLOAD: '/api/v1/files/upload',

    // Query endpoints (using .NET backend)
    QUERIES: '/api/v1/query/',
    ACTIVE_QUERIES: '/api/v1/queries/active',
    QUERY_HISTORY: '/api/v1/queries/history',
    QUERY_BY_ID: '/api/v1/query/{id}',
    QUERY_COMMUNICATIONS: '/api/v1/queries/{id}/communications',
    QUERY_TASKS: '/api/v1/queries/{id}/tasks',
    QUERY_APPROVE: '/api/v1/query/{id}/approve',  // .NET backend endpoint
    QUERY_FULL_RESULT: '/api/v1/query/{id}/result',  // .NET backend endpoint

    // Export endpoints (new .NET API endpoints)
    PDF_GENERATE: '/api/v1/export/pdf',
    PDF_GENERATE_STRATEGY: '/api/v1/export/pdf',
    PDF_GENERATE_COLUMBUS: '/api/v1/export/pdf',
    PDF_QUERY: '/api/v1/pdf/query/{id}',
    PPTX_GENERATE: '/api/v1/export/pptx',
  }
};

// Helper function to build full API URLs
export const buildApiUrl = (endpoint: string, params?: Record<string, string>): string => {
  let url = `${API_CONFIG.BASE_URL}${endpoint}`;
  
  // Replace path parameters like {id} with actual values
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, value);
    });
  }
  
  return url;
};

// Pre-built common API URLs
export const API_URLS = {
  // Health endpoints
  HEALTH: buildApiUrl(API_CONFIG.ENDPOINTS.HEALTH),
  READY: buildApiUrl(API_CONFIG.ENDPOINTS.READY),

  // Main Query endpoints (Columbus.Agents.Api)
  QUERY_SUBMIT: buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_SUBMIT),
  getQueryStatusUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_STATUS, { id: queryId }),
  getQueryResultUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_RESULT, { id: queryId }),
  // EventSource cannot set headers, so the JWT is passed via ?access_token=
  // (same pattern as getMeetingLiveStreamUrl; accepted by the backend's
  // OnMessageReceived hook, contracts.md §1).
  getQueryStreamUrl: (queryId: string, accessToken: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_STREAM, { id: queryId }) +
    `?access_token=${encodeURIComponent(accessToken)}`,
  getQueryFollowupUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_FOLLOWUP, { id: queryId }),

  // Agent endpoints
  AGENTS: buildApiUrl(API_CONFIG.ENDPOINTS.AGENTS),
  getAgentUrl: (agentId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.AGENT_BY_ID, { id: agentId }),
  AGENTS_HEALTH: buildApiUrl(API_CONFIG.ENDPOINTS.AGENTS_HEALTH),
  getAgentHealthUrl: (agentId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.AGENT_HEALTH, { id: agentId }),
  AGENTS_METRICS: buildApiUrl(API_CONFIG.ENDPOINTS.AGENTS_METRICS),
  AGENTS_TOKEN_METRICS: buildApiUrl(API_CONFIG.ENDPOINTS.AGENTS_TOKEN_METRICS),

  // Project endpoints
  PROJECTS: buildApiUrl(API_CONFIG.ENDPOINTS.PROJECTS),
  getProjectUrl: (projectId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.PROJECT_BY_ID, { id: projectId }),
  getProjectQueryUrl: (projectId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.PROJECT_QUERY, { id: projectId }),
  getProjectRefreshUrl: (projectId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.PROJECT_REFRESH, { id: projectId }),
  getProjectHistoryUrl: (projectId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.PROJECT_HISTORY, { id: projectId }),
  getProjectChatMessagesUrl: (projectId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.PROJECT_CHAT_MESSAGES, { id: projectId }),

  // Auth endpoints
  AUTH_LOGIN: buildApiUrl(API_CONFIG.ENDPOINTS.AUTH_LOGIN),
  AUTH_LOGOUT: buildApiUrl(API_CONFIG.ENDPOINTS.AUTH_LOGOUT),
  AUTH_ME: buildApiUrl(API_CONFIG.ENDPOINTS.AUTH_ME),
  AUTH_ME_PROFILE: buildApiUrl(API_CONFIG.ENDPOINTS.AUTH_ME_PROFILE),
  USERS: buildApiUrl(API_CONFIG.ENDPOINTS.USERS),

  // Live meeting endpoints
  getMeetingLiveStartUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_LIVE_START, { id: meetingId }),
  getMeetingLiveStopUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_LIVE_STOP, { id: meetingId }),
  getMeetingLiveSlideUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_LIVE_SLIDE, { id: meetingId }),
  getMeetingLiveTranscriberUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_LIVE_TRANSCRIBER, { id: meetingId }),
  getMeetingLiveTeamsTokenUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_LIVE_TEAMS_TOKEN, { id: meetingId }),
  getMeetingLiveSpeechTokenUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_LIVE_SPEECH_TOKEN, { id: meetingId }),
  getMeetingLiveStreamUrl: (meetingId: string, accessToken: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_LIVE_STREAM, { id: meetingId }) +
    `?access_token=${encodeURIComponent(accessToken)}`,
  getMeetingTranscriptSegmentsUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_TRANSCRIPT_SEGMENTS, { id: meetingId }),
  getMeetingInsightsUrl: (meetingId: string, scope: 'live' | 'prep' = 'live') =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_INSIGHTS, { id: meetingId }) + `?scope=${scope}`,
  getMeetingInsightsRegenerateUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_INSIGHTS_REGENERATE, { id: meetingId }),
  getMeetingQuestionsUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_QUESTIONS, { id: meetingId }),
  getMeetingPersonaBriefingUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_BRIEFING_PERSONA, { id: meetingId }),
  getMeetingQuestionActUrl: (meetingId: string, questionId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_QUESTION_ACT, { id: meetingId, qid: questionId }),
  getMeetingChatStreamUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_CHAT_STREAM, { id: meetingId }),
  getMeetingOutcomesApproveUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_OUTCOMES_APPROVE, { id: meetingId }),
  getMeetingOutcomesExportPdfUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_OUTCOMES_EXPORT_PDF, { id: meetingId }),
  getMeetingOutcomesExportPptxUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_OUTCOMES_EXPORT_PPTX, { id: meetingId }),

  // Meeting endpoints
  MEETINGS: buildApiUrl(API_CONFIG.ENDPOINTS.MEETINGS),
  getMeetingUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_BY_ID, { id: meetingId }),
  getMeetingAgendaUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_AGENDA, { id: meetingId }),
  getMeetingAgendaGenerateUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_AGENDA_GENERATE, { id: meetingId }),
  getMeetingAttendeesGenerateUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_ATTENDEES_GENERATE, { id: meetingId }),
  getMeetingBriefingGenerateUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_BRIEFING_GENERATE, { id: meetingId }),
  getMeetingTranscriptUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_TRANSCRIPT, { id: meetingId }),
  getMeetingSummaryUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_SUMMARY, { id: meetingId }),
  getMeetingSummaryGenerateUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_SUMMARY_GENERATE, { id: meetingId }),
  getMeetingChatUrl: (meetingId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.MEETING_CHAT, { id: meetingId }),

  // File endpoints
  FILES: buildApiUrl(API_CONFIG.ENDPOINTS.FILES),
  FILE_UPLOAD: buildApiUrl(API_CONFIG.ENDPOINTS.FILE_UPLOAD),
  getProjectFileUploadUrl: (projectId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.PROJECT_FILE_UPLOAD, { id: projectId }),
  getProjectFilesUrl: (projectId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.PROJECT_FILES, { id: projectId }),
  getFileProcessUrl: (fileId: string, forceReprocess: boolean = false) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.FILE_PROCESS, { id: fileId }) + `?force_reprocess=${forceReprocess}`,
  FILE_UPLOAD_GLOBAL: buildApiUrl(API_CONFIG.ENDPOINTS.FILE_UPLOAD_GLOBAL),

  // Query endpoints (legacy - may not be implemented)
  QUERIES: buildApiUrl(API_CONFIG.ENDPOINTS.QUERIES),
  ACTIVE_QUERIES: buildApiUrl(API_CONFIG.ENDPOINTS.ACTIVE_QUERIES),
  QUERY_HISTORY: buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_HISTORY),
  getQueryUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_BY_ID, { id: queryId }),
  getQueryCommunicationsUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_COMMUNICATIONS, { id: queryId }),
  getQueryTasksUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_TASKS, { id: queryId }),
  getQueryApproveUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_APPROVE, { id: queryId }),
  getQueryFullResultUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.QUERY_FULL_RESULT, { id: queryId }),

  // Export endpoints
  PDF_GENERATE: buildApiUrl(API_CONFIG.ENDPOINTS.PDF_GENERATE),
  PDF_GENERATE_STRATEGY: buildApiUrl(API_CONFIG.ENDPOINTS.PDF_GENERATE_STRATEGY),
  PDF_GENERATE_COLUMBUS: buildApiUrl(API_CONFIG.ENDPOINTS.PDF_GENERATE_COLUMBUS),
  getPdfQueryUrl: (queryId: string) =>
    buildApiUrl(API_CONFIG.ENDPOINTS.PDF_QUERY, { id: queryId }),
  PPTX_GENERATE: buildApiUrl(API_CONFIG.ENDPOINTS.PPTX_GENERATE),
};